'use client';
import React, { useState, useRef, useEffect } from 'react';

interface ParkCard {
  id: string;
  label: string;
  labelColor: string;
  labelBg: string;
  title: string;
  subtitle: string;
  gradientFrom: string;
  gradientTo: string;
  photoCaption: string;
  image?: string;
}

interface ParkDetail {
  id: string;
  topLabel: string;
  tag: string;
  tagColor: string;
  tagBg: string;
  tagDot: string;
  site: string;
  survey: string;
  result: string;
  resultCaption: string;
  notableFinds: string;
  imageLabel: string;
  imageGradient: string;
  image?: string;
}

const parks: ParkCard[] = [
  {
    id: 'pk-1',
    label: 'HIGHEST DENSITY',
    labelColor: '#4a7c59',
    labelBg: 'rgba(74,124,89,0.15)',
    title: 'Old Renner · Dallas',
    subtitle: '~190 observations',
    gradientFrom: '#2d4a35',
    gradientTo: '#6b8f5e',
    photoCaption: 'Old Renner Park canopy',
    image: '/assets/images/CleanShot_2026-05-29_at_10.15.09_2x-1780067732660.png',
  },
  {
    id: 'pk-2',
    label: 'LAKESIDE EDGE',
    labelColor: '#3a7a6e',
    labelBg: 'rgba(58,122,110,0.15)',
    title: 'Shady Grove · Azle',
    subtitle: '33 observations · Eagle Mountain Lake',
    gradientFrom: '#1e3d38',
    gradientTo: '#5a8c7a',
    photoCaption: 'Shady Grove shoreline',
    image: '/assets/images/CleanShot_2026-05-29_at_10.18.43_2x-1780067941720.png',
  },
  {
    id: 'pk-3',
    label: 'RIPARIAN CORRIDOR',
    labelColor: '#5a7a3a',
    labelBg: 'rgba(90,122,58,0.15)',
    title: 'Cottonwood · Richardson',
    subtitle: '71 observations',
    gradientFrom: '#2e4020',
    gradientTo: '#7a9c50',
    photoCaption: 'Cottonwood creek',
    image: '/assets/images/CleanShot_2026-05-29_at_10.19.44_2x-1780067999041.png',
  },
  {
    id: 'pk-4',
    label: 'ORNAMENTAL BASELINE',
    labelColor: '#7a6a3a',
    labelBg: 'rgba(122,106,58,0.15)',
    title: 'Curtis · University Park',
    subtitle: '63 observations',
    gradientFrom: '#3d3520',
    gradientTo: '#9c8c50',
    photoCaption: 'Curtis Park pond',
    image: '/assets/images/CleanShot_2026-05-29_at_10.28.01_2x-1780068701524.png',
  },
  {
    id: 'pk-5',
    label: 'URBAN CREEK',
    labelColor: '#3a6e7a',
    labelBg: 'rgba(58,110,122,0.15)',
    title: 'Churchill · Dallas',
    subtitle: '~97 observations',
    gradientFrom: '#1e3840',
    gradientTo: '#5a8090',
    photoCaption: 'Churchill Park creek',
    image: '/assets/images/Churchill_Park-1787629417675.png',
  },
  {
    id: 'pk-6',
    label: 'LIGHTLY SURVEYED',
    labelColor: '#6a7a4a',
    labelBg: 'rgba(106,122,74,0.15)',
    title: 'Frankford · Dallas',
    subtitle: '~32 observations',
    gradientFrom: '#2e3a20',
    gradientTo: '#6a7a4a',
    photoCaption: 'Frankford Park woodland',
    image: '/assets/images/Frankford_Park-1787629418269.png',
  },
  {
    id: 'pk-7',
    label: 'POND HABITAT',
    labelColor: '#2f7468',
    labelBg: 'rgba(47,116,104,0.15)',
    title: 'Bentwood · Dallas',
    subtitle: '~97 observations',
    gradientFrom: '#1e3d38',
    gradientTo: '#4a8478',
    photoCaption: 'Bentwood Park pond',
    image: '/assets/images/Bentwood_Park-1787629107576.png',
  },
  {
    id: 'pk-8',
    label: 'MANAGED TURF',
    labelColor: '#7a8a4a',
    labelBg: 'rgba(122,138,74,0.15)',
    title: 'Cheyenne · Dallas',
    subtitle: '~30 observations',
    gradientFrom: '#3a4525',
    gradientTo: '#8a9c5e',
    photoCaption: 'Cheyenne Park turf',
    image: '/assets/images/Cheyenne_Park-1787629107866.png',
  },
  {
    id: 'pk-9',
    label: 'POLLINATOR FIND',
    labelColor: '#6e7a3a',
    labelBg: 'rgba(110,122,58,0.15)',
    title: 'Preston Green · Dallas',
    subtitle: '~51 observations',
    gradientFrom: '#343d1c',
    gradientTo: '#7a8a4a',
    photoCaption: 'Preston Green meadow',
    image: '/assets/images/Preston_Green_Park-1787629108152.png',
  },
  {
    id: 'pk-10',
    label: 'NATIVE MEADOW',
    labelColor: '#8a7a3a',
    labelBg: 'rgba(138,122,58,0.15)',
    title: 'Bent Tree Meadow · Dallas',
    subtitle: '~71 observations',
    gradientFrom: '#3d3820',
    gradientTo: '#9a8c50',
    photoCaption: 'Bent Tree Meadow',
    image: '/assets/images/Bent_Tree_Meadow_Park-1787629106947.png',
  },
  {
    id: 'pk-11',
    label: 'REPTILE STANDOUT',
    labelColor: '#7a5a3a',
    labelBg: 'rgba(122,90,58,0.15)',
    title: 'Lively Pointe · Irving',
    subtitle: '~67 observations',
    gradientFrom: '#3a2d20',
    gradientTo: '#8a6e4a',
    photoCaption: 'Lively Pointe pond',
    image: '/assets/images/Lively_Pointe_Park-1787629107558.png',
  },
];

const parkDetails: ParkDetail[] = [
  {
    id: 'pk-1',
    topLabel: 'MAY 2026 · OLD RENNER PARK, DALLAS',
    tag: 'HIGHEST-DENSITY SURVEY',
    tagColor: '#4a7c59',
    tagBg: 'rgba(74,124,89,0.15)',
    tagDot: '#4a7c59',
    site: "Old Renner anchors the project's Dallas core — a mature-canopy neighborhood park threaded by a creek corridor. Baldcypress, Cedar Elm, and pecan dominate the canopy; the understory carries native woodsorrels, straggler daisy, and roughleaf dogwood.",
    survey: 'Two coordinated field days, May 17 and May 19, with the full Dallas team. Every plant, insect, bird, and mammal encountered was photographed and uploaded for community verification.',
    result: '~190',
    resultCaption: 'observations logged — the highest of any single park',
    notableFinds: 'Eastern Cottontail, Red Imported Fire Ant, chinkapin oak.',
    imageLabel: 'Old Renner Park — survey deployment',
    imageGradient: 'linear-gradient(145deg, #1a3020 0%, #2d4a35 40%, #4a7040 70%, #6b8f5e 100%)',
    image: '/assets/images/CleanShot_2026-05-29_at_10.15.09_2x-1780067732660.png',
  },
  {
    id: 'pk-2',
    topLabel: 'MAY 2026 · SHADY GROVE PARK, AZLE',
    tag: 'LAKESIDE EDGE',
    tagColor: '#3a7a6e',
    tagBg: 'rgba(58,122,110,0.15)',
    tagDot: '#3a7a6e',
    site: "Shady Grove anchors the inventory's western edge in Azle, on a margin of Eagle Mountain Lake. The survey captured the disturbed-meadow-and-grass community typical of a lakeside park — Bermuda grass, common hedge parsley, Cretanweed, and Santa Maria feverfew — rather than wetland specialists.",
    survey: 'Single-day deployment, May 17, with a focused field team. Shoreline plants, lakeside birds, and aquatic insects were the priority targets.',
    result: '33',
    resultCaption: 'observations logged — a distinct lake-cove ecosystem captured for the baseline',
    notableFinds: 'Straggler Daisy, blackjack oak, Great-tailed Grackle.',
    imageLabel: 'Shady Grove Park — Eagle Mountain Lake shoreline',
    imageGradient: 'linear-gradient(145deg, #0e2420 0%, #1e3d38 40%, #3a6a5e 70%, #5a8c7a 100%)',
    image: '/assets/images/CleanShot_2026-05-29_at_10.18.43_2x-1780067941720.png',
  },
  {
    id: 'pk-3',
    topLabel: 'MAY 2026 · COTTONWOOD PARK, RICHARDSON',
    tag: 'RIPARIAN CORRIDOR',
    tagColor: '#5a7a3a',
    tagBg: 'rgba(90,122,58,0.15)',
    tagDot: '#5a7a3a',
    site: 'Cottonwood threads along a creek in Richardson — a riparian corridor of mature canopy and consistent water access. The survey captured a textbook creek-ecosystem signature: Eastern Cottonwood (the park\'s namesake tree), black willow, and baldcypress lining the water; Cedar Elm and sugar hackberry filling the adjacent canopy.',
    survey: 'Single-day deployment, May 20, focused on the creek corridor and adjacent canopy. Endorsed by Richardson Parks Director Shohn Rodgers.',
    result: '71',
    resultCaption: "observations logged — the dataset's clearest creek-ecosystem signal",
    notableFinds: 'Eastern Cottonwood, black willow, eastern redcedar.',
    imageLabel: 'Cottonwood Park — creek corridor',
    imageGradient: 'linear-gradient(145deg, #1a2810 0%, #2e4020 40%, #507030 70%, #7a9c50 100%)',
    image: '/assets/images/CleanShot_2026-05-29_at_10.19.44_2x-1780067999041.png',
  },
  {
    id: 'pk-4',
    topLabel: 'MAY 2026 · CURTIS PARK, UNIVERSITY PARK',
    tag: 'ORNAMENTAL BASELINE',
    tagColor: '#7a6a3a',
    tagBg: 'rgba(122,106,58,0.15)',
    tagDot: '#7a6a3a',
    site: "Curtis Park sits within University Park's planned, manicured landscape — heavily ornamental, with cultivated trees and managed lawns. The survey documented the cultivated-versus-naturalized mix that defines a managed suburban park: Heavenly bamboo, Lilac chaste tree, crape-myrtles, and Cedar Elm alongside ornamental ground covers.",
    survey: 'Single-day deployment, May 20. Documented the cultivated-versus-naturalized species mix that defines a managed suburban park.',
    result: '63',
    resultCaption: 'observations logged — a critical control for cultivated-vs-wild comparison',
    notableFinds: 'Mallard, Heavenly bamboo, Lilac chaste tree.',
    imageLabel: 'Curtis Park — ornamental landscape',
    imageGradient: 'linear-gradient(145deg, #251e0a 0%, #3d3520 40%, #6a5c30 70%, #9c8c50 100%)',
    image: '/assets/images/CleanShot_2026-05-29_at_10.28.01_2x-1780068701524.png',
  },
  {
    id: 'pk-5',
    topLabel: 'MAY 2026 · CHURCHILL PARK, DALLAS',
    tag: 'URBAN CREEK',
    tagColor: '#3a6e7a',
    tagBg: 'rgba(58,110,122,0.15)',
    tagDot: '#3a6e7a',
    site: "A Dallas neighborhood park threaded by a creek — pecan, bur oak, and eastern redcedar form the canopy over native Missouri violet, with invasive Quihoui privet established throughout.",
    survey: "Single field day, May 11, with the Dallas team.",
    result: '~97',
    resultCaption: 'observations logged — one of the more thoroughly sampled Dallas sites',
    notableFinds: 'a freshwater crayfish, Missouri violet, Quihoui privet.',
    imageLabel: 'Churchill Park — creek corridor',
    imageGradient: 'linear-gradient(145deg, #0e2428 0%, #1e3840 40%, #3a6068 70%, #5a8090 100%)',
    image: '/assets/images/Churchill_Park-1787629417675.png',
  },
  {
    id: 'pk-6',
    topLabel: 'MAY 2026 · FRANKFORD PARK, DALLAS',
    tag: 'LIGHTLY SURVEYED',
    tagColor: '#6a7a4a',
    tagBg: 'rgba(106,122,74,0.15)',
    tagDot: '#6a7a4a',
    site: "A smaller north-Dallas park sampled across two early field days — oak canopy with native roughleaf dogwood beneath. The lightest dataset of the Dallas parks, and a clear target for deeper coverage in the Year 2 replication.",
    survey: "Two field days, May 10 and 11.",
    result: '~32',
    resultCaption: 'observations logged — the lightest single-park count, flagged for fuller coverage in Year 2',
    notableFinds: 'Eastern Cottontail, roughleaf dogwood, American Robin.',
    imageLabel: 'Frankford Park — oak woodland',
    imageGradient: 'linear-gradient(145deg, #1a2410 0%, #2e3a20 40%, #4e5e30 70%, #6a7a4a 100%)',
    image: '/assets/images/Frankford_Park-1787629418269.png',
  },
  {
    id: 'pk-7',
    topLabel: 'MAY 2026 · BENTWOOD PARK, DALLAS',
    tag: 'POND HABITAT',
    tagColor: '#2f7468',
    tagBg: 'rgba(47,116,104,0.15)',
    tagDot: '#2f7468',
    site: "Built around standing water — baldcypress (a tree that grows in water) and sugar hackberry ring a pond and creek edge, with aquatic life turning up right alongside the canopy.",
    survey: "Two field days, May 14 and 19.",
    result: '~97',
    resultCaption: 'observations logged — among the highest, around a pond habitat',
    notableFinds: 'Pond Slider, baldcypress, sugar hackberry.',
    imageLabel: 'Bentwood Park — pond edge',
    imageGradient: 'linear-gradient(145deg, #0e2420 0%, #1e3d38 40%, #356055 70%, #4a8478 100%)',
    image: '/assets/images/Bentwood_Park-1787629107576.png',
  },
  {
    id: 'pk-8',
    topLabel: 'MAY 2026 · CHEYENNE PARK, DALLAS',
    tag: 'MANAGED TURF',
    tagColor: '#7a8a4a',
    tagBg: 'rgba(122,138,74,0.15)',
    tagDot: '#7a8a4a',
    site: "The northernmost Dallas site and the most species-even in the whole survey — nearly every record was a different species. Dominated by managed turf grasses (Bermuda, St. Augustine, buffalograss): a clean baseline for a heavily mowed neighborhood park.",
    survey: "Single field day, May 17.",
    result: '~30',
    resultCaption: "observations logged — the survey's most species-even park",
    notableFinds: 'Bermuda grass, buffalograss, Cedar Elm.',
    imageLabel: 'Cheyenne Park — managed turf',
    imageGradient: 'linear-gradient(145deg, #20280f 0%, #3a4525 40%, #5e6e38 70%, #8a9c5e 100%)',
    image: '/assets/images/Cheyenne_Park-1787629107866.png',
  },
  {
    id: 'pk-9',
    topLabel: 'MAY 2026 · PRESTON GREEN PARK, DALLAS',
    tag: 'POLLINATOR FIND',
    tagColor: '#6e7a3a',
    tagBg: 'rgba(110,122,58,0.15)',
    tagDot: '#6e7a3a',
    site: "A mostly herbaceous Dallas park — common hedge parsley, prickly lettuce, and native sumacs over little tree canopy — that nonetheless turned up one of the survey's standout pollinators.",
    survey: "Single field day, May 20.",
    result: '~51',
    resultCaption: 'observations logged — a herbaceous site that produced a standout pollinator',
    notableFinds: 'Pipevine Swallowtail, upright prairie coneflower, common hedge parsley.',
    imageLabel: 'Preston Green Park — meadow edge',
    imageGradient: 'linear-gradient(145deg, #1c2410 0%, #343d1c 40%, #586830 70%, #7a8a4a 100%)',
    image: '/assets/images/Preston_Green_Park-1787629108152.png',
  },
  {
    id: 'pk-10',
    topLabel: 'MAY 2026 · BENT TREE MEADOW PARK, DALLAS',
    tag: 'NATIVE MEADOW',
    tagColor: '#8a7a3a',
    tagBg: 'rgba(138,122,58,0.15)',
    tagDot: '#8a7a3a',
    site: "The westernmost Dallas park and the most wildflower-rich — Texas lantana, red yucca, and flathead rabbit tobacco scattered through flatter meadow grasses. It lives up to its name.",
    survey: "Single field day, May 20.",
    result: '~71',
    resultCaption: "observations logged — the survey's most wildflower-rich park",
    notableFinds: 'Texas lantana, red yucca, flathead rabbit tobacco.',
    imageLabel: 'Bent Tree Meadow Park — native wildflowers',
    imageGradient: 'linear-gradient(145deg, #24200f 0%, #3d3820 40%, #685e30 70%, #9a8c50 100%)',
    image: '/assets/images/Bent_Tree_Meadow_Park-1787629106947.png',
  },
  {
    id: 'pk-11',
    topLabel: 'MAY 2026 · LIVELY POINTE PARK, IRVING',
    tag: 'REPTILE STANDOUT',
    tagColor: '#7a5a3a',
    tagBg: 'rgba(122,90,58,0.15)',
    tagDot: '#7a5a3a',
    site: "Irving's site, surveyed in a single late-May push around a pond. Native Texas ash, cedar elm, and bottomland post oak frame it — and a reptile, not a plant or bird, led the animal counts.",
    survey: "Single field day, May 27.",
    result: '~67',
    resultCaption: 'observations logged — the only park where a reptile led the animal counts',
    notableFinds: 'Texas Spiny Lizard, Eastern Pondhawk, Texas ash.',
    imageLabel: 'Lively Pointe Park — pond and woodland',
    imageGradient: 'linear-gradient(145deg, #241a0f 0%, #3a2d20 40%, #604830 70%, #8a6e4a 100%)',
    image: '/assets/images/Lively_Pointe_Park-1787629107558.png',
  },
];

const GallerySection: React.FC = () => {
  const [activeCard, setActiveCard] = useState<string>('pk-1');
  const [visibleCard, setVisibleCard] = useState<string>('pk-1');
  const [fading, setFading] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll<HTMLElement>('.gallery-item');
            items.forEach((item, i) => {
              item.classList.add('will-animate');
              setTimeout(() => {
                item.classList.add('animated');
                item.classList.remove('will-animate');
              }, i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleThumbClick = (id: string) => {
    if (id === activeCard) return;
    setFading(true);
    setActiveCard(id);
    setTimeout(() => {
      setVisibleCard(id);
      setFading(false);
    }, 200);
    setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const detail = parkDetails.find((d) => d.id === visibleCard) ?? parkDetails[0];

  return (
    <section
      id="catalog"
      ref={sectionRef}
      className="py-20 md:py-28 px-6 bg-satellite topo-bg"
      style={{ scrollMarginTop: '96px' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid md:grid-cols-2 gap-8 mb-14 items-end">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-canopy/8 border border-canopy/20 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-canopy" />
              <span className="text-canopy text-[10px] font-bold uppercase tracking-[0.35em]">
                Field Findings by Park
              </span>
            </div>
            <h2 className="section-headline">
              What ten days in<br />
              <em className="not-italic font-light" style={{ color: 'var(--canopy-mid)' }}>
                the field revealed.
              </em>
            </h2>
          </div>
          <p className="text-umber/70 text-base md:text-lg leading-relaxed font-light max-w-md">
            Across eleven parks in five cities, every observation was logged, photographed, and
            verified by the global iNaturalist community — each site with its own signature, from
            creek corridors and pond habitats to managed turf and a lakeside cove.
          </p>
        </div>

        {/* Park Card Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {parks.map((park, i) => (
            <div
              key={park.id}
              className={`gallery-item gallery-thumb ${activeCard === park.id ? 'active' : ''}`}
              style={{
                transitionDelay: `${i * 80}ms`,
                cursor: 'pointer',
                outline: activeCard === park.id ? '2px solid #A0522D' : '2px solid transparent',
                outlineOffset: '2px',
                borderRadius: '12px',
                transition: 'outline 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
              }}
              onClick={() => handleThumbClick(park.id)}
              role="button"
              tabIndex={0}
              aria-label={`View park: ${park.title}`}
              aria-pressed={activeCard === park.id}
              onKeyDown={(e) => e.key === 'Enter' && handleThumbClick(park.id)}
              onMouseEnter={(e) => {
                if (activeCard !== park.id) {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                }
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              }}
            >
              {/* Gradient placeholder image area */}
              <div
                className="relative aspect-[4/3] overflow-hidden flex items-end justify-start"
                style={{
                  background: park.image
                    ? undefined
                    : `linear-gradient(135deg, ${park.gradientFrom} 0%, ${park.gradientTo} 60%, #a8c090 100%)`,
                  borderRadius: '10px 10px 0 0',
                }}
              >
                {park.image && (
                  <img
                    src={park.image}
                    alt={park.photoCaption}
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    style={{ borderRadius: '10px 10px 0 0' }}
                  />
                )}
                {!park.image && (
                  /* Subtle texture overlay */
                  <div
                    className="absolute inset-0 opacity-20"
                    style={{
                      backgroundImage:
                        'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.04) 8px, rgba(255,255,255,0.04) 9px)',
                    }}
                  />
                )}
                {/* Photo caption label */}
                <span
                  className="relative z-10 m-2.5 px-2 py-1 rounded text-[9px] font-mono tracking-wide text-white/80"
                  style={{ background: 'rgba(0,0,0,0.5)' }}
                >
                  {park.photoCaption}
                </span>
              </div>

              {/* Meta */}
              <div className="p-3 bg-white" style={{ borderRadius: '0 0 10px 10px' }}>
                <div className="flex items-center justify-between mb-1.5">
                  <span
                    className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{
                      color: park.labelColor,
                      background: park.labelBg,
                    }}
                  >
                    {park.label}
                  </span>
                </div>
                <p className="text-umber text-xs font-semibold leading-tight">{park.title}</p>
                <p className="text-umber/50 text-[10px] mt-0.5">{park.subtitle}</p>
              </div>
            </div>
          ))}
          {/* Twelfth slot — summary + CTA tile */}
          <a
            href="https://www.inaturalist.org/projects/north-texas-biodiversity-inventory-ntbi"
            target="_blank"
            rel="noopener noreferrer"
            className="gallery-item rounded-xl overflow-hidden flex flex-col justify-between p-5"
            style={{
              background: 'linear-gradient(150deg, #1a3020 0%, #2d4a35 100%)',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            aria-label="View the full inventory on iNaturalist"
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)';
              (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.18)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
            }}
          >
            <div>
              <span className="text-white/55 text-[9px] font-bold uppercase tracking-[0.3em]">
                The full picture
              </span>
              <p
                className="text-white text-lg font-semibold leading-snug mt-3"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                853 observations · 221 species across 11 parks in 5 cities.
              </p>
            </div>
            <span className="text-white text-[11px] font-bold uppercase tracking-widest mt-4 inline-flex items-center gap-1.5">
              View on iNaturalist <span aria-hidden="true">↗</span>
            </span>
          </a>
        </div>

        {/* Detail Panel — cross-fades between parks */}
        <div ref={detailRef}>
          <div
            className="detail-panel open"
            style={{
              opacity: fading ? 0 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            <div className="bg-white rounded-2xl border border-canopy/15 overflow-hidden shadow-xl">
              <div className="grid lg:grid-cols-5 gap-0">
                {/* Left: image placeholder */}
                <div
                  className="lg:col-span-2 relative min-h-[260px] lg:min-h-[320px] flex items-end justify-start"
                  style={{ background: detail.image ? '#1a3020' : detail.imageGradient }}
                >
                  {detail.image && (
                    <img
                      src={detail.image}
                      alt={detail.imageLabel}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                  )}
                  {/* Texture overlay */}
                  {!detail.image && (
                    <div
                      className="absolute inset-0 opacity-15"
                      style={{
                        backgroundImage:
                          'repeating-linear-gradient(60deg, transparent, transparent 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 11px)',
                      }}
                    />
                  )}
                  {/* Caption badge */}
                  <div className="relative z-10 m-4 rounded-lg px-3 py-1.5" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}>
                    <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest">
                      May 2026
                    </p>
                    <p className="text-white/90 text-xs font-medium font-mono">
                      {detail.imageLabel}
                    </p>
                  </div>
                </div>

                {/* Right: story */}
                <div className="lg:col-span-3 p-6 md:p-8 flex flex-col justify-between">
                  <div>
                    {/* Top label + tag */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="text-umber/60 text-[10px] font-bold uppercase tracking-widest">
                        {detail.topLabel}
                      </span>
                      <span
                        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                        style={{
                          color: detail.tagColor,
                          background: detail.tagBg,
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ background: detail.tagDot }}
                        />
                        {detail.tag}
                      </span>
                    </div>

                    {/* Three-section story */}
                    <div className="space-y-4">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-canopy mb-1">
                          The Site
                        </p>
                        <p className="text-umber/80 text-sm leading-relaxed">{detail.site}</p>
                      </div>
                      <div className="w-full h-px bg-canopy/10" />
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-umber mb-1">
                          The Survey
                        </p>
                        <p className="text-umber/80 text-sm leading-relaxed">{detail.survey}</p>
                      </div>
                      <div className="w-full h-px bg-canopy/10" />
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-saffron-dark mb-1">
                            Result
                          </p>
                          <p className="font-display font-bold text-2xl text-umber">{detail.result}</p>
                          <p className="text-umber/60 text-xs">{detail.resultCaption}</p>
                          {detail.notableFinds && (
                            <p className="mt-1 text-[14px] italic leading-snug" style={{ fontFamily: 'Fraunces, serif', color: 'var(--ash)' }}>
                              Notable finds: {detail.notableFinds}
                            </p>
                          )}
                        </div>
                        <div className="flex-1 h-px bg-saffron/30" />
                        <a
                          href="https://www.inaturalist.org/projects/north-texas-biodiversity-inventory-ntbi"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-saffron px-5 py-2.5 text-sm font-bold whitespace-nowrap"
                        >
                          View on iNaturalist
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;